import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { DatePicker } from "src/core/components/custom-ui/date-picker";
import { FormCombobox } from "src/core/components/custom-ui/form-combobox";
import { FormInput } from "src/core/components/custom-ui/form-input";
import { FormSelect } from "src/core/components/custom-ui/form-select";
import { Button } from "src/core/components/ui/button";
import { ISelectOption } from "src/core/constants/options";
import { useRiskOptions } from "src/core/contexts/RiskOptionsContext";
import { useCountryOptions } from "src/core/hooks/common/useCountryOptions";
import { useLegalStatusOptionsPPH } from "src/core/hooks/common/useLegalStatusOptionsPPH";
import { useUpdatePPH } from "src/core/hooks/folder/useUpdatePPH";
import { useRequiredTenantPaths } from "src/core/hooks/routing/useTenantPaths";
import { getGenderOptions } from "src/core/lib/options/getGenderOptions";
import { cn } from "src/core/lib/utils";
import { withRequiredOverride } from "src/core/overrides";

const DocumentsUploadFieldsetOverride = withRequiredOverride(
  "components.folder.DocumentsUploadFieldset"
);

type UpdatePPHProps = {
  onSuccess?: () => void;
  /** Définit si le formulaire doit être ouvert dans un modal. */
  setOpen?: (open: boolean) => void;
};

/**
 * Composant pour ajouter une partie prenante (stakeholder) à un dossier.
 * Formulaire complet avec toutes les informations nécessaires.
 */
export function UpdatePPH({ onSuccess, setOpen }: UpdatePPHProps) {
  const { t } = useTranslation();

  const paths = useRequiredTenantPaths();

  const genderOptions = getGenderOptions(t);
  const {
    countryOptions,
    loading: countryLoading,
  }: { countryOptions: ISelectOption[]; loading: boolean } = useCountryOptions();
  const { legalStatusOptions, loading: legalStatusOptionsPPHLoading } = useLegalStatusOptionsPPH();
  const { activityWithRiskOptions, countryWithRiskOptions } = useRiskOptions();

  const {
    form,
    onSubmit,
    isReadOnly,
    requiredDocuments,
    incompleteFields,
    isLoading,
    missingInfo,
    missingDocuments,
    duplicateFolder,
  } = useUpdatePPH({ onSuccess });

  const folderId = duplicateFolder?.id;
  const folderUrl = folderId ? paths.folder(folderId) : null;

  return (
    <div className="folder-stakeholders-data">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="section-title">
            {t("folder.stakeholders.documents.title")}
            {missingDocuments > 0 && <span className="item-counter-alert">{missingDocuments}</span>}
          </div>
          <DocumentsUploadFieldsetOverride
            requiredDocuments={requiredDocuments}
            disabled={isReadOnly}
            incompleteFields={incompleteFields}
          />
          <div className="section-title">
            {t("folder.stakeholders.fields.title")}

            {missingInfo > 0 && <span className="item-counter-alert">{missingInfo}</span>}
          </div>

          <div className="grid grid-cols-5 gap-x-10 gap-y-20 mb-20 mt-10">
            <FormSelect
              control={form.control}
              name="title"
              required
              softRequired={incompleteFields.has("title")}
              label={t("form.addStakeholderFull.title")}
              options={genderOptions}
              className="w-full"
              disabled={isReadOnly}
            />
            <FormInput
              control={form.control}
              name="firstName"
              required
              label={t("form.addStakeholderFull.firstName")}
              className="w-full"
              disabled={isReadOnly}
            />
            <FormInput
              control={form.control}
              name="birthName"
              label={t("form.addStakeholderFull.birthName")}
              className="w-full"
              disabled={isReadOnly}
            />
            <FormInput
              control={form.control}
              name="lastName"
              required
              label={t("form.addStakeholderFull.lastName")}
              className="w-full"
              disabled={isReadOnly}
            />
            <FormCombobox
              control={form.control}
              name="citizenshipCountryCode"
              required
              softRequired={incompleteFields.has("citizenshipCountryCode")}
              label={t("form.addStakeholderFull.citizenshipCountryCode")}
              options={countryOptions}
              isLoading={countryLoading}
              className="w-full"
              disabled={isReadOnly}
              isInModal
            />
          </div>

          <div className="grid grid-cols-5 gap-x-10 gap-y-20 mb-20">
            <DatePicker
              name="birthDate"
              required
              softRequired={incompleteFields.has("birthDate")}
              label={t("form.addStakeholderFull.birthDate")}
              className="w-full"
              control={form.control}
              allowFutureDates={false}
              allowPastDates={true}
              maxDate={new Date()}
              disabled={isReadOnly}
            />
            <FormCombobox
              control={form.control}
              name="residenceCountryCode"
              required
              softRequired={incompleteFields.has("residenceCountryCode")}
              label={t("form.addStakeholderFull.residenceCountryCode")}
              options={countryWithRiskOptions}
              className="w-full"
              withBadge
              disabled={isReadOnly}
              isInModal
            />
            <FormCombobox
              control={form.control}
              name="birthCountryCode"
              required
              softRequired={incompleteFields.has("birthCountryCode")}
              label={t("form.addStakeholderFull.birthCountryCode")}
              options={countryOptions}
              isLoading={countryLoading}
              className="w-full"
              disabled={isReadOnly}
              isInModal
            />
            <div className="col-span-2">
              <FormInput
                control={form.control}
                name="birthPlace"
                label={t("form.addStakeholderFull.birthPlace")}
                className="w-full"
                disabled={isReadOnly}
              />
            </div>
          </div>

          <div className="w-full h-[1px] bg-border"></div>
          <div className="grid grid-cols-5 gap-x-10 gap-y-20 my-20">
            <div className="col-span-2">
              <FormCombobox
                control={form.control}
                name={"activityCode"}
                required
                softRequired={incompleteFields.has("activityCode")}
                label={t("form.entreprise.activite")}
                options={activityWithRiskOptions}
                className="w-full"
                withBadge
                disabled={isReadOnly}
                isInModal
              />
            </div>
            <div className="col-span-2">
              <FormSelect
                control={form.control}
                name={"personLegalFormCode"}
                required
                softRequired={incompleteFields.has("personLegalFormCode")}
                label={t("form.entreprise.formeJuridique")}
                options={legalStatusOptions}
                isLoading={legalStatusOptionsPPHLoading}
                className="w-full"
                disabled={isReadOnly}
              />
            </div>
            <div className="flex flex-col gap-y-10">
              <FormInput
                control={form.control}
                name={"registration"}
                label={t("form.entreprise.siren")}
                className="w-full"
                disabled={isReadOnly}
              />
              {!isReadOnly &&
                form.formState.errors?.registration?.type === "duplicate" &&
                folderUrl && (
                  <Link
                    to={folderUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="action-link"
                  >
                    {t("form.createClient.seeFolder")}
                  </Link>
                )}
            </div>
          </div>
          <div className="grid grid-cols-5 gap-x-10 gap-y-20 mb-20">
            <div className="col-span-2">
              <FormInput
                control={form.control}
                name={"customString2"}
                required
                softRequired={incompleteFields.has("customString2")}
                label={t("form.entreprise.adresse")}
                className="w-full"
                disabled={isReadOnly}
              />
            </div>
            <FormInput
              control={form.control}
              name={"customString1"}
              required
              softRequired={incompleteFields.has("customString1")}
              label={t("form.entreprise.codePostal")}
              className="w-full"
              disabled={isReadOnly}
            />
            <FormCombobox
              control={form.control}
              name="customCountryCode1"
              required
              softRequired={incompleteFields.has("customCountryCode1")}
              label={t("form.entreprise.pays")}
              options={countryOptions}
              isLoading={countryLoading}
              className="w-full"
              disabled={isReadOnly}
              isInModal
            />
          </div>
          <div
            className={cn(
              "flex flex-row justify-center gap-x-40 border-t border-border pt-10 sticky bottom-0 bg-gray-xlight"
            )}
          >
            <Button
              variant="link"
              onClick={(e) => {
                e.preventDefault();
                setOpen?.(false);
              }}
            >
              {t(isReadOnly ? "global.form.close" : "global.form.cancel")}
            </Button>
            {!isReadOnly && (
              <Button type="submit" loading={isLoading}>
                {t("form.addStakeholderFull.submit")}
              </Button>
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
